// RFC 9457 problem+json — mirrors the API's ProblemJsonFilter output and the
// stable `code` taxonomy (error-codes.ts). The console branches on `code`, never
// on `detail` (human text) — codes are the contract.
export const ErrorCodes = {
  INTERNAL: "CSA-1000",
  VALIDATION_FAILED: "CSA-1001",
  UNAUTHENTICATED: "CSA-1002",
  FORBIDDEN: "CSA-1003",
  NOT_FOUND: "CSA-1004",
  CONFLICT: "CSA-1005",
  RATE_LIMITED: "CSA-1006",
  STEP_UP_REQUIRED: "CSA-1007",
  FOUR_EYES_REQUIRED: "CSA-1008",
  PARTY_ALREADY_EXISTS: "CSA-1009",
  /** CLIENT-ORIGIN ONLY — the API never emits this. The request never reached a
   *  server: DNS failure, connection refused, TLS error, or (overwhelmingly the
   *  common case) the device is offline. Deliberately in the CSA-0xxx range so it
   *  can never collide with a server code. See networkProblemError(). */
  NETWORK_UNAVAILABLE: "CSA-0001",
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

export interface ProblemDocument {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  code: string;
  traceId?: string;
  errors?: unknown;
  /** RFC 9457 extension members (the API spreads structured details at the top level —
   *  e.g. `existingPartyId` on a CSA-1009). Preserved as-is so the console can read them. */
  [key: string]: unknown;
}

/** A failed /v1 call, carrying the parsed problem document for typed handling. */
export class ProblemError extends Error {
  readonly status: number;
  readonly code: string;
  readonly traceId?: string;
  readonly problem: ProblemDocument;

  constructor(problem: ProblemDocument) {
    super(problem.detail || problem.title);
    this.name = "ProblemError";
    this.status = problem.status;
    this.code = problem.code;
    this.traceId = problem.traceId;
    this.problem = problem;
  }

  is(code: ErrorCode): boolean {
    return this.code === code;
  }
}

/**
 * A fetch REJECTION (no Response at all) as a ProblemError.
 *
 * Why this exists: `toProblemError` only runs on a non-2xx `Response`. When fetch
 * itself rejects — offline, DNS failure, connection refused — there is no Response,
 * so before this the raw `TypeError: Failed to fetch` propagated uncaught and every
 * `err instanceof ProblemError` branch in all four SPAs was DEAD offline. Users saw
 * a spinner that never resolved or a form that silently did nothing.
 *
 * `status: 0` is deliberate and load-bearing: it must not collide with any real HTTP
 * status, so the client's own recovery ladder (401 refresh, 403 step-up, 409
 * four-eyes) cannot mistake an offline failure for a governance signal and retry it.
 */
export function networkProblemError(cause: unknown): ProblemError {
  const err = new ProblemError({
    type: "about:blank",
    title: "Network unavailable",
    status: 0,
    detail: "Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.",
    instance: "",
    code: ErrorCodes.NETWORK_UNAVAILABLE,
  });
  // Keep the original TypeError reachable for logging without leaking it to the UI.
  (err as { cause?: unknown }).cause = cause;
  return err;
}

/** Parse a non-2xx Response into a ProblemError, tolerating non-JSON bodies. */
export async function toProblemError(res: Response): Promise<ProblemError> {
  let body: Partial<ProblemDocument> = {};
  try {
    body = (await res.json()) as Partial<ProblemDocument>;
  } catch {
    // Non-JSON error (gateway, network shim) — synthesize a minimal problem.
  }
  return new ProblemError({
    ...body, // preserve RFC 9457 extension members (e.g. existingPartyId)
    type: body.type ?? "about:blank",
    title: body.title ?? res.statusText,
    status: body.status ?? res.status,
    detail: body.detail ?? res.statusText,
    instance: body.instance ?? "",
    code: body.code ?? ErrorCodes.INTERNAL,
    traceId: body.traceId,
    errors: body.errors,
  });
}
